import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Lock, CheckCircle, XCircle, Spinner, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import PageTransition from '@/components/PageTransition';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
        if (response.data.email) {
          setEmail(response.data.email);
        }
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsNotMatch'));
      return;
    }
    
    if (password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(t('auth.passwordChanged'));
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <PageTransition>
        <div className={`min-h-screen flex items-center justify-center ${isDark ? '' : 'bg-gray-50'}`}>
          <div className="text-center">
            <Spinner size={40} className={`animate-spin mx-auto mb-4 ${isDark ? 'text-white' : 'text-gray-600'}`} />
            <p className={isDark ? 'text-white/60' : 'text-gray-600'}>{t('auth.verifyingLink')}</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Invalid token
  if (!token || !tokenValid) {
    return (
      <PageTransition>
        <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? '' : 'bg-gray-50'}`}>
          <div className={`max-w-md w-full p-8 rounded-2xl text-center ${
            isDark ? 'glass-card' : 'bg-white border border-gray-200 shadow-lg'
          }`}>
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} weight="fill" className="text-red-500" />
            </div>
            <h1 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.invalidOrExpiredLink')}
            </h1>
            <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {t('auth.invalidLinkDesc')}
            </p>
            <Link to="/forgot-password">
              <Button className="btn-premium">
                {t('auth.newRequest')}
              </Button>
            </Link>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Success state
  if (success) {
    return (
      <PageTransition>
        <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? '' : 'bg-gray-50'}`}>
          <div className={`max-w-md w-full p-8 rounded-2xl text-center ${
            isDark ? 'glass-card' : 'bg-white border border-gray-200 shadow-lg'
          }`}>
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} weight="fill" className="text-green-500" />
            </div>
            <h1 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.passwordChanged')}
            </h1>
            <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {t('auth.passwordChangedDesc')}
            </p>
            <Link to="/login">
              <Button className="btn-premium">
                {t('auth.login')}
              </Button>
            </Link>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className={`min-h-screen flex items-center justify-center p-4 relative z-10 ${isDark ? '' : 'bg-gray-50'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="flex justify-end mb-6">
            <ThemeToggle />
          </div>
          
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Sparkle size={20} weight="fill" className="text-white" />
              </div>
              <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>SIMBLYO</span>
            </Link>
            <h1 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.newPasswordTitle')}
            </h1>
            {email && (
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
                {t('auth.for')} <strong>{email}</strong>
              </p>
            )}
          </div>

          <div className={`p-8 rounded-2xl ${
            isDark 
              ? 'glass-card' 
              : 'bg-white border border-gray-200 shadow-lg'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                  {t('auth.newPassword')}
                </Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className={
                    isDark 
                      ? 'bg-white/[0.03] border-white/[0.08] focus:border-violet-500/50' 
                      : 'bg-white border-gray-200'
                  }
                  data-testid="reset-password-input"
                />
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  {t('auth.minChars')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                  {t('auth.confirmPassword')}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className={
                    isDark 
                      ? 'bg-white/[0.03] border-white/[0.08] focus:border-violet-500/50' 
                      : 'bg-white border-gray-200'
                  }
                  data-testid="reset-confirm-password-input"
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-500">{t('auth.passwordsNotMatch')}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                className="w-full btn-premium"
                data-testid="reset-submit-btn"
              >
                {loading ? t('auth.modifying') : t('auth.modifyPassword')}
              </Button>
              
              <div className="text-center pt-2">
                <Link 
                  to="/login" 
                  className={`inline-flex items-center gap-1 text-sm hover:underline ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  <ArrowLeft size={14} />
                  {t('common.backToLogin')}
                </Link>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
}
